/**
 * Customer Hooks - T067
 *
 * TanStack Query hooks for customer CRUD operations
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';
import { useActiveTenantId } from './useTenantContext';
import type { TenantKey } from '../services/api/keys';
import type {
  CreateCustomerInput,
  UpdateCustomerInput,
  CustomerListQuery as CustomerListQueryType,
  CreateAddressInput,
  UpdateAddressInput,
} from '@armard/shared';

// Re-export for convenience
export type CustomerListQuery = Partial<CustomerListQueryType>;

// ─────────────────────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────────────────────

export interface Customer {
  id: string;
  customerNumber: string;
  companyName: string;
  contactFirstName: string | null;
  contactLastName: string | null;
  email: string | null;
  phone: string | null;
  defaultPaymentDays: number;
  notes: string | null;
  isArchived: boolean;
  createdAt: string;
  updatedAt: string;
  version: number;
}

export interface Address {
  id: string;
  customerId: string;
  addressType: 'billing' | 'shipping';
  isDefault: boolean;
  street: string;
  street2: string | null;
  postalCode: string;
  city: string;
  country: string;
  createdAt: string;
  updatedAt: string;
}

export interface CustomerListResult {
  customers: Customer[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

// ─────────────────────────────────────────────────────────────
// QUERY KEYS
// ─────────────────────────────────────────────────────────────

export const customerKeys = {
  all: (t: TenantKey) => ['customers', t] as const,
  lists: (t: TenantKey) => [...customerKeys.all(t), 'list'] as const,
  list: (t: TenantKey, query: CustomerListQuery) => [...customerKeys.lists(t), query] as const,
  details: (t: TenantKey) => [...customerKeys.all(t), 'detail'] as const,
  detail: (t: TenantKey, id: string) => [...customerKeys.details(t), id] as const,
  addresses: (t: TenantKey, customerId: string) =>
    [...customerKeys.detail(t, customerId), 'addresses'] as const,
  search: (t: TenantKey, q: string) => [...customerKeys.all(t), 'search', q] as const,
};

// ─────────────────────────────────────────────────────────────
// CUSTOMER HOOKS
// ─────────────────────────────────────────────────────────────

/**
 * Hook to list customers with pagination and filtering
 */
export function useCustomerList(query: CustomerListQuery = {}) {
  const tenantId = useActiveTenantId();
  const fullQuery = {
    page: query.page ?? 1,
    limit: query.limit ?? 40,
    ...query,
  };

  return useQuery({
    queryKey: customerKeys.list(tenantId, fullQuery),
    enabled: tenantId !== null,
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set('page', String(fullQuery.page));
      params.set('limit', String(fullQuery.limit));
      if (fullQuery.q) params.set('q', fullQuery.q);
      if (fullQuery.isArchived !== undefined) params.set('isArchived', String(fullQuery.isArchived));
      if (fullQuery.sortBy) params.set('sortBy', fullQuery.sortBy);
      if (fullQuery.sortOrder) params.set('sortOrder', fullQuery.sortOrder);

      return api.get<CustomerListResult>(`/customers?${params.toString()}`);
    },
  });
}

/**
 * Hook to get a single customer by ID
 */
export function useCustomer(id: string) {
  const tenantId = useActiveTenantId();
  return useQuery({
    queryKey: customerKeys.detail(tenantId, id),
    queryFn: () => api.get<Customer>(`/customers/${id}`),
    enabled: !!id && tenantId !== null,
  });
}

/**
 * Hook to search customers (for autocomplete)
 */
export function useCustomerSearch(q: string, limit = 10) {
  const tenantId = useActiveTenantId();
  return useQuery({
    queryKey: customerKeys.search(tenantId, q),
    queryFn: () => api.get<Customer[]>(`/customers/search?q=${encodeURIComponent(q)}&limit=${limit}`),
    enabled: q.length >= 2 && tenantId !== null,
  });
}

/**
 * Hook to create a customer
 */
export function useCreateCustomer() {
  const queryClient = useQueryClient();
  const tenantId = useActiveTenantId();

  return useMutation({
    mutationFn: (input: CreateCustomerInput) => api.post<Customer>('/customers', input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: customerKeys.lists(tenantId) });
    },
  });
}

/**
 * Hook to update a customer
 */
export function useUpdateCustomer() {
  const queryClient = useQueryClient();
  const tenantId = useActiveTenantId();

  return useMutation({
    mutationFn: ({ id, ...input }: UpdateCustomerInput & { id: string }) =>
      api.patch<Customer>(`/customers/${id}`, input),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: customerKeys.lists(tenantId) });
      queryClient.setQueryData(customerKeys.detail(tenantId, data.id), data);
    },
  });
}

/**
 * Hook to archive/unarchive a customer
 */
export function useArchiveCustomer() {
  const queryClient = useQueryClient();
  const tenantId = useActiveTenantId();

  return useMutation({
    mutationFn: ({ id, isArchived, version }: { id: string; isArchived: boolean; version: number }) =>
      api.patch<Customer>(`/customers/${id}/archive`, { isArchived, version }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: customerKeys.lists(tenantId) });
      queryClient.setQueryData(customerKeys.detail(tenantId, data.id), data);
    },
  });
}

// ─────────────────────────────────────────────────────────────
// ADDRESS HOOKS
// ─────────────────────────────────────────────────────────────

/**
 * Hook to list addresses for a customer
 */
export function useCustomerAddresses(customerId: string) {
  const tenantId = useActiveTenantId();
  return useQuery({
    queryKey: customerKeys.addresses(tenantId, customerId),
    queryFn: () => api.get<Address[]>(`/customers/${customerId}/addresses`),
    enabled: !!customerId && tenantId !== null,
  });
}

/**
 * Hook to create an address
 */
export function useCreateAddress(customerId: string) {
  const queryClient = useQueryClient();
  const tenantId = useActiveTenantId();

  return useMutation({
    mutationFn: (input: CreateAddressInput) =>
      api.post<Address>(`/customers/${customerId}/addresses`, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: customerKeys.addresses(tenantId, customerId) });
    },
  });
}

/**
 * Hook to update an address
 */
export function useUpdateAddress(customerId: string) {
  const queryClient = useQueryClient();
  const tenantId = useActiveTenantId();

  return useMutation({
    mutationFn: ({ addressId, ...input }: UpdateAddressInput & { addressId: string }) =>
      api.patch<Address>(`/customers/${customerId}/addresses/${addressId}`, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: customerKeys.addresses(tenantId, customerId) });
    },
  });
}

/**
 * Hook to delete an address
 */
export function useDeleteAddress(customerId: string) {
  const queryClient = useQueryClient();
  const tenantId = useActiveTenantId();

  return useMutation({
    mutationFn: (addressId: string) =>
      api.delete(`/customers/${customerId}/addresses/${addressId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: customerKeys.addresses(tenantId, customerId) });
    },
  });
}
